import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
    Loader2,
    Mail,
    Calendar,
    Eye,
    CheckCircle,
    XCircle,
    UserCheck,
    Flag,
    FileText,
    Clock,
    Download,
} from "lucide-react";
import Swal from "sweetalert2";
import api from "../../../utils/api";
import { format } from "date-fns";

export default function MyCustomer() {
    const [requests, setRequests] = useState([]);
    const [paralegals, setParalegals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState({});
    const [showDetails, setShowDetails] = useState(null);

    // Custom Assign Modal State
    const [showAssignModal, setShowAssignModal] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");

    const fetchRequests = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get("/requests/lawyer-requests");
            if (res.data.success) {
                setRequests(res.data.data || []);
            }
        } catch (err) {
            Swal.fire("Error", "Failed to load requests.", "error");
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchParalegals = useCallback(async () => {
        try {
            const res = await api.get("/paralegals");
            if (res.data.success) {
                setParalegals(res.data.data || []);
            }
        } catch (err) {
            console.error("Failed to load paralegals:", err);
        }
    }, []);

    useEffect(() => {
        fetchRequests();
        fetchParalegals();
    }, [fetchRequests, fetchParalegals]);

    // عمليات الحالة
    const handleAction = async (requestId, action, paralegalId = null) => {
        const actionLabels = {
            accept: "Accept",
            reject: "Reject",
            "assign-paralegal": "Assign",
            complete: "Complete",
        };

        const result = await Swal.fire({
            icon: "warning",
            title: `Are you sure?`,
            text: `You are about to ${actionLabels[action]} this request.`,
            showCancelButton: true,
            confirmButtonText: `Yes, ${actionLabels[action]}`,
            cancelButtonText: "Cancel",
            confirmButtonColor: action === "accept" ? "#10b981" : action === "reject" ? "#ef4444" : "#3b82f6",
        });

        if (!result.isConfirmed) return;

        setProcessing((prev) => ({ ...prev, [requestId]: action }));

        try {
            let endpoint = `/requests/${requestId}/${action}`;
            const body = paralegalId ? { paralegal_id: paralegalId } : {};

            const res = await api.post(endpoint, body);

            if (res.data.success) {
                await Swal.fire({
                    icon: "success",
                    title: "Success!",
                    text: `Request has been ${action.replace("-", " ")}d.`,
                    timer: 2000,
                    showConfirmButton: false,
                });
                fetchRequests();
            }
        } catch (err) {
            const msg = err.response?.data?.message || `Failed to ${action} request.`;
            Swal.fire("Error", msg, "error");
        } finally {
            setProcessing((prev) => ({ ...prev, [requestId]: null }));
        }
    };

    // عرض تفاصيل الطلب
    const openDetails = (request) => {
        setShowDetails(request);
    };

    const closeDetails = () => {
        setShowDetails(null);
    };

    // Assign Paralegal - Custom Modal
    const handleAssignClick = (requestId) => {
        if (paralegals.length === 0) {
            Swal.fire("No Paralegals", "No paralegals available to assign.", "info");
            return;
        }
        setShowAssignModal(requestId);
        setSearchTerm("");
    };

    const closeAssignModal = () => {
        setShowAssignModal(null);
        setSearchTerm("");
    };

    const confirmAssign = (requestId, paralegalId) => {
        handleAction(requestId, "assign-paralegal", paralegalId);
        closeAssignModal();
    };

    const filteredParalegals = paralegals.filter(p =>
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // ألوان الحالة
    const getStatusConfig = (status) => {
        const config = {
            pending: { color: "bg-yellow-100 text-yellow-800", icon: <Clock className="w-3 h-3" /> },
            accepted: { color: "bg-blue-100 text-blue-800", icon: <CheckCircle className="w-3 h-3" /> },
            assigned: { color: "bg-purple-100 text-purple-800", icon: <UserCheck className="w-3 h-3" /> },
            completed: { color: "bg-green-100 text-green-800", icon: <Flag className="w-3 h-3" /> },
            rejected: { color: "bg-red-100 text-red-800", icon: <XCircle className="w-3 h-3" /> },
        };
        return config[status] || config.pending;
    };

    if (loading) {
        return (
            <section className="p-6 min-h-screen">
                <div className="max-w-7xl mx-auto">
                    <div className="animate-pulse">
                        <div className="h-8 bg-gray-200 rounded w-64 mb-4"></div>
                        <div className="space-y-4">
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="h-24 bg-white rounded-xl"></div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="p-6 min-h-screen">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">My Customer Requests</h2>
                        <p className="text-gray-600 mt-1">Manage and track consultation requests</p>
                    </div>
                </div>

                {requests.length === 0 ? (
                    <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
                        <div className="w-20 h-20 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                            <FileText className="w-10 h-10 text-gray-400" />
                        </div>
                        <p className="text-gray-600 font-medium">No requests yet</p>
                        <p className="text-gray-500 text-sm mt-1">New client requests will appear here</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {requests.map((req, i) => {
                            const statusConfig = getStatusConfig(req.status);
                            const isProcessing = processing[req.id];
                            return (
                                <motion.div
                                    key={req.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: i * 0.05 }}
                                    className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow"
                                >
                                    <div className="flex items-center justify-between">
                                        {/* Client Info */}
                                        <div className="flex items-center space-x-4 flex-1">
                                            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                                                <span className="text-blue-600 font-bold text-sm">
                                                    {req.client_name.split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase()}
                                                </span>
                                            </div>
                                            <div className="flex-1">
                                                <h4 className="font-semibold text-gray-900">{req.client_name}</h4>
                                                <div className="flex items-center gap-3 text-sm text-gray-600 mt-1">
                                                    <span className="flex items-center gap-1">
                                                        <Mail className="w-3.5 h-3.5" />
                                                        {req.client_email}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Case & Form */}
                                        <div className="flex-1 text-center">
                                            <p className="text-sm font-medium text-gray-900">{req.case_type.name}</p>
                                            <p className="text-xs text-green-600 font-bold">{parseFloat(req.case_type.price).toFixed(2)} USD</p>
                                        </div>

                                        {/* Actions */}
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => openDetails(req)}
                                                className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                                                title="View Details"
                                            >
                                                <Eye className="w-4 h-4" />
                                            </button>

                                            {/* Files & Date */}
                                            <div className="flex-1 text-center">
                                                <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                    <FileText className="w-3 h-3" />
                                                    {req.files_count} file{req.files_count !== 1 ? "s" : ""}
                                                </div>
                                            </div>

                                            {/* Status */}
                                            <div className="flex-1 text-center">
                                                <div className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
                                                    {statusConfig.icon}
                                                    {req.status.charAt(0).toUpperCase() + req.status.slice(1)}
                                                </div>
                                            </div>

                                            {req.status === "pending" && (
                                                <>
                                                    <button
                                                        onClick={() => handleAction(req.id, "accept")}
                                                        disabled={!!isProcessing}
                                                        className="px-3 py-1.5 bg-green-100 text-green-700 rounded-lg font-medium text-xs hover:bg-green-200 disabled:opacity-50 flex items-center gap-1"
                                                    >
                                                        {isProcessing === "accept" ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
                                                        Accept
                                                    </button>
                                                    <button
                                                        onClick={() => handleAction(req.id, "reject")}
                                                        disabled={!!isProcessing}
                                                        className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg font-medium text-xs hover:bg-red-200 disabled:opacity-50 flex items-center gap-1"
                                                    >
                                                        {isProcessing === "reject" ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />}
                                                        Reject
                                                    </button>
                                                </>
                                            )}

                                            {req.status === "accepted" && !req.assigned_to_paralegal && (
                                                <button
                                                    onClick={() => handleAssignClick(req.id)}
                                                    disabled={!!isProcessing}
                                                    className="px-3 py-1.5 bg-purple-100 text-purple-700 rounded-lg font-medium text-xs hover:bg-purple-200 disabled:opacity-50 flex items-center gap-1"
                                                >
                                                    {isProcessing === "assign-paralegal" ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserCheck className="w-3 h-3" />}
                                                    Assign
                                                </button>
                                            )}

                                            {req.status === "assigned" && (
                                                <button
                                                    onClick={() => handleAction(req.id, "complete")}
                                                    disabled={!!isProcessing}
                                                    className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg font-medium text-xs hover:bg-blue-200 disabled:opacity-50 flex items-center gap-1"
                                                >
                                                    {isProcessing === "complete" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Flag className="w-3 h-3" />}
                                                    Complete
                                                </button>
                                            )}

                                            {(req.status === "completed" || req.status === "rejected") && (
                                                <span className="text-gray-400 text-xs">—</span>
                                            )}
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                )}

                {/* Details Modal */}
                {showDetails && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
                        >
                            <div className="flex items-center justify-between mb-5">
                                <h3 className="text-xl font-bold text-gray-900">Request Details</h3>
                                <button
                                    onClick={closeDetails}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <XCircle className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="space-y-5">
                                <div className="flex items-center space-x-4">
                                    <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center">
                                        <span className="text-blue-600 font-bold text-lg">
                                            {showDetails.client_name.split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase()}
                                        </span>
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900">{showDetails.client_name}</h4>
                                        <p className="text-sm text-gray-600 flex items-center gap-1">
                                            <Mail className="w-3.5 h-3.5" /> {showDetails.client_email}
                                        </p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-xs text-gray-500">Title</p>
                                        <p className="font-medium text-gray-900">{showDetails.title}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500">Case Type</p>
                                        <p className="font-medium text-gray-900">{showDetails.case_type.name}</p>
                                        <p className="text-xs text-green-600 font-bold">{parseFloat(showDetails.case_type.price).toFixed(2)} USD</p>
                                    </div>
                                </div>

                                <div>
                                    <p className="text-xs text-gray-500">Form</p>
                                    <p className="font-medium text-gray-900">{showDetails.form_name}</p>
                                </div>

                                <div>
                                    <p className="text-xs text-gray-500">Description</p>
                                    <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">{showDetails.description}</p>
                                </div>

                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-xs text-gray-500">Submitted On</p>
                                        <p className="text-sm font-medium text-gray-900 flex items-center gap-1">
                                            <Calendar className="w-3.5 h-3.5" />
                                            {format(new Date(showDetails.created_at), "MMMM d, yyyy 'at' h:mm a")}
                                        </p>
                                    </div>
                                    <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                        <FileText className="w-3 h-3" />
                                        {showDetails.files_count} file{showDetails.files_count !== 1 ? "s" : ""}
                                    </div>
                                </div>

                                {/* Attached Files */}
                                {showDetails.files && showDetails.files.length > 0 && (
                                    <div>
                                        <p className="text-xs text-gray-500 mb-2">Attached Files</p>
                                        <div className="space-y-2">
                                            {showDetails.files.map((file) => (
                                                <div
                                                    key={file.id}
                                                    className="flex items-center justify-between bg-gray-50 p-3 rounded-lg hover:bg-gray-100 transition"
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <FileText className="w-4 h-4 text-blue-600" />
                                                        <div>
                                                            <p className="text-sm font-medium text-gray-900">{file.name}</p>
                                                            <p className="text-xs text-gray-500">
                                                                {file.size} • {file.type}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <a
                                                        href={file.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm font-medium"
                                                    >
                                                        <Download className="w-4 h-4" />
                                                        Download
                                                    </a>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="flex justify-end pt-4">
                                    <button
                                        onClick={closeDetails}
                                        className="px-5 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}

                {/* Custom Assign Paralegal Modal */}
                {showAssignModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="bg-white rounded-xl p-6 w-full max-w-md max-h-[80vh] overflow-y-auto"
                        >
                            <div className="flex items-center justify-between mb-5">
                                <h3 className="text-xl font-bold text-gray-900">Assign to Paralegal</h3>
                                <button
                                    onClick={closeAssignModal}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <XCircle className="w-5 h-5" />
                                </button>
                            </div>

                            {/* Search */}
                            <div className="mb-4">
                                <input
                                    type="text"
                                    placeholder="Search by name or email..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            {/* Paralegals List */}
                            <div className="space-y-3 max-h-96 overflow-y-auto">
                                {filteredParalegals.length === 0 ? (
                                    <p className="text-center text-gray-500 py-8">No paralegals found</p>
                                ) : (
                                    filteredParalegals.map((p) => (
                                        <div
                                            key={p.id}
                                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition"
                                        >
                                            <div className="flex items-center space-x-3">
                                                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                                    <span className="text-blue-600 font-semibold text-sm">
                                                        {p.name.split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase()}
                                                    </span>
                                                </div>
                                                <div>
                                                    <p className="font-medium text-gray-900">{p.name}</p>
                                                    <p className="text-xs text-gray-500">{p.email}</p>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => confirmAssign(showAssignModal, p.id)}
                                                disabled={processing[showAssignModal] === "assign-paralegal"}
                                                className="px-3 py-1.5 bg-purple-600 text-white text-xs font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1"
                                            >
                                                {processing[showAssignModal] === "assign-paralegal" ? (
                                                    <Loader2 className="w-3 h-3 animate-spin" />
                                                ) : (
                                                    <UserCheck className="w-3 h-3" />
                                                )}
                                                Assign
                                            </button>
                                        </div>
                                    ))
                                )}
                            </div>

                            <div className="mt-5 flex justify-end">
                                <button
                                    onClick={closeAssignModal}
                                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                                >
                                    Cancel
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </div>
        </section>
    );
}